import type { CallOutcomeExtra, CallOutcomeOption } from './CallOutcomePicker';
import type { SoftphoneStatus } from './softphoneClient.types';
import { ContactAvatar } from '../Avatar/ContactAvatar';
import { cn } from '../cn';
import { formatPhoneDisplay } from '../ContactDetail/formatPhoneDisplay';
import Button from '../Input2/Button';
import { Communication, General, Media, Users } from '../StrokeIcons';
import { CallOutcomePicker } from './CallOutcomePicker';
import { SoftphoneKeypad } from './SoftphoneKeypad';

export type { CallOutcomeExtra, CallOutcomeOption };

export interface SoftphoneProps {
  contactName?: string;
  phoneNumber: string;
  status: SoftphoneStatus;
  durationSec: number;
  isMuted: boolean;
  error: string | null;
  /** When true, show a recording-consent indicator while the call is live. */
  isRecording?: boolean;
  /** Contact photo for the avatar; falls back to initials from contactName when absent. */
  imageSrc?: string | null;
  /**
   * The number being dialed is the contact's org ("firm") number, used because the person has no
   * direct number of their own. Renders a prominent "Firm number" badge so the rep knows they're
   * reaching the company, not the person.
   */
  isFirmNumber?: boolean;
  /** The number this call is/was placed from. Renders a "Calling from …" line; hidden when absent. */
  callerNumber?: string | null;
  /**
   * The contact's pinned from-number is no longer in the rep's bucket: show a subtle warning.
   * The number is still what this call goes out from. Rotation pulls the pin back into the bucket
   * on the next unanswered call — but NOT once the contact's number has settled (they answered, or
   * the rep chose it via Change), so a settled-then-released number needs a manual re-pin.
   */
  callerNumberStale?: boolean;
  /** Present = render the small guarded "Change" affordance; disabled while a call is live. */
  onChangeNumber?: () => void;
  outcomes: ReadonlyArray<CallOutcomeOption>;
  onToggleMute: () => void;
  onHangup: () => void;
  /** Send a DTMF digit on the live call. When absent the keypad is hidden entirely. */
  onSendDigits?: (digits: string) => void;
  onSelectOutcome: (value: string, extra?: CallOutcomeExtra) => void;
  onClose: () => void;
  className?: string;
}

const STATUS_LABEL: Record<SoftphoneStatus, string> = {
  idle: 'Ready',
  connecting: 'Connecting…',
  ringing: 'Ringing…',
  active: 'Connected',
  ended: 'Call ended',
  error: 'Call failed',
};

const STATUS_DOT: Record<SoftphoneStatus, string> = {
  idle: 'bg-gray-400',
  connecting: 'bg-yellow-400 animate-pulse',
  ringing: 'bg-yellow-400 animate-pulse',
  active: 'bg-green-500',
  ended: 'bg-gray-400',
  error: 'bg-red-500',
};

function formatDuration(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function Softphone({
  contactName,
  phoneNumber,
  status,
  durationSec,
  isMuted,
  error,
  isRecording,
  imageSrc,
  isFirmNumber,
  callerNumber,
  callerNumberStale,
  onChangeNumber,
  outcomes,
  onToggleMute,
  onHangup,
  onSendDigits,
  onSelectOutcome,
  onClose,
  className,
}: SoftphoneProps) {
  const inCall = status === 'connecting' || status === 'ringing' || status === 'active';
  const finished = status === 'ended' || status === 'error';

  return (
    <div className={cn('flex w-80 flex-col gap-4 rounded-xl bg-white p-5 dark:bg-gray-900', className)}>
      <ContactAvatar
        shape="circle"
        className="mx-auto size-16"
        // Avatar derives initials by splitting its alt on spaces, so the full display name
        // yields correct initials ("Ada Lovelace" → "AL"). A bare phone number would make a
        // meaningless "+" initial, so the nameless case gets a person glyph instead.
        firstName={contactName ?? null}
        lastName={null}
        imageSrc={imageSrc}
        placeholder={contactName ? undefined : <Users.User01 />}
        data-test="softphone-avatar"
      />

      <div className="flex flex-col items-center gap-1 text-center">
        <span className="truncate text-base font-semibold text-gray-900 dark:text-gray-100">
          {contactName ?? phoneNumber}
        </span>
        {contactName && <span className="truncate text-sm text-gray-500 dark:text-gray-400">{phoneNumber}</span>}
        {isFirmNumber && (
          <span
            className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-700 dark:bg-amber-500/15 dark:text-amber-400"
            data-test="firm-number-badge">
            <General.Building02 className="size-3.5" />
            Firm number
          </span>
        )}
      </div>

      <div className="flex items-center justify-center gap-2 text-sm text-gray-600 dark:text-gray-300">
        <span className={cn('h-2 w-2 rounded-full transition-colors', STATUS_DOT[status])} />
        <span>{STATUS_LABEL[status]}</span>
        {status === 'active' && <span className="tabular-nums text-gray-400">{formatDuration(durationSec)}</span>}
      </div>

      {inCall && isRecording && (
        <div className="flex items-center justify-center gap-1.5 text-xs text-red-500" data-test="recording-indicator">
          <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
          <span>Recording</span>
        </div>
      )}

      {callerNumber && (
        <div className="flex flex-col items-center gap-0.5">
          <div
            className="flex items-center justify-center gap-1.5 text-xs text-gray-500 dark:text-gray-400"
            data-test="caller-number">
            <span className="tabular-nums">Calling from {formatPhoneDisplay(callerNumber)}</span>
            {onChangeNumber && (
              <Button
                size="2xs"
                text
                disabled={inCall}
                tooltip={inCall ? 'Finish the call to change the number' : undefined}
                onClick={onChangeNumber}
                data-test="change-caller-number">
                Change
              </Button>
            )}
          </div>
          {callerNumberStale && (
            <p className="text-center text-xs text-amber-600 dark:text-amber-500" data-test="caller-number-stale">
              This number is no longer assigned to you
            </p>
          )}
        </div>
      )}

      {error && <p className="text-center text-sm text-red-500">{error}</p>}

      {inCall && onSendDigits && (
        // Keys stay disabled until the call is answered: DTMF has no media path to ride before
        // then, and Twilio's sendDigits throws pre-media (the hook's try/catch is the backstop).
        <SoftphoneKeypad onPressDigit={onSendDigits} disabled={status !== 'active'} />
      )}

      {inCall && (
        <div className="flex items-center justify-center gap-3">
          <Button
            shape="pill"
            size="xl"
            tone={isMuted ? 'filled' : 'outline'}
            color={isMuted ? 'yellow' : 'gray'}
            disabled={status !== 'active'}
            icon={isMuted ? <Media.MicrophoneOff01 /> : <Media.Microphone01 />}
            tooltip={isMuted ? 'Unmute' : 'Mute'}
            aria-label={isMuted ? 'Unmute' : 'Mute'}
            onClick={onToggleMute}
            data-test="mute-button"
          />
          <Button
            shape="pill"
            size="xl"
            color="red"
            icon={<Communication.PhoneHangUp02 />}
            tooltip="Hang up"
            aria-label="Hang up"
            onClick={onHangup}
            data-test="hangup-button"
          />
        </div>
      )}

      {finished && <CallOutcomePicker outcomes={outcomes} onSelect={onSelectOutcome} onSkip={onClose} />}
    </div>
  );
}
